/**
 * Adapted from React Bits Card Swap (David Haz).
 * https://reactbits.dev/components/card-swap
 * See LICENSE.react-bits. Keeps the drop/promote/return GSAP choreography,
 * with controlled selection, keyboard navigation and reduced-motion support.
 */
import {
  Children,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useMediaQuery } from '@mui/material';
import { gsap } from 'gsap';

interface CardSwapProps {
  children: ReactNode;
  activeIndex: number;
  onChange: (index: number) => void;
  playing: boolean;
  delay?: number;
}

export default function CardSwap({
  children,
  activeIndex,
  onChange,
  playing,
  delay = 7000,
}: CardSwapProps) {
  const cards = Children.toArray(children);
  const container = useRef<HTMLDivElement>(null);
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const previous = useRef(activeIndex);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(true);
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  const count = cards.length;

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) =>
      setVisible(entry.isIntersecting)
    );
    if (container.current) observer.observe(container.current);
    const visibility = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', visibility);
    };
  }, []);

  useEffect(() => {
    if (
      !playing ||
      reducedMotion ||
      hovered ||
      focused ||
      !visible ||
      !pageVisible ||
      count < 2
    )
      return;
    const timer = window.setTimeout(
      () => onChange((activeIndex + 1) % count),
      delay
    );
    return () => window.clearTimeout(timer);
  }, [
    playing,
    reducedMotion,
    hovered,
    focused,
    visible,
    pageVisible,
    count,
    activeIndex,
    delay,
    onChange,
  ]);

  useLayoutEffect(() => {
    const oldIndex = previous.current;
    const changed = oldIndex !== activeIndex;
    const timeline = gsap.timeline();
    refs.current.forEach((element, index) => {
      if (!element) return;
      const slot = (index - activeIndex + count) % count;
      const position = {
        x: Math.min(slot, 3) * 12,
        y: -Math.min(slot, 3) * 17,
        z: -slot * 50,
        rotateZ: slot === 0 ? 0 : -1.2,
        opacity: slot < 3 ? 1 : 0,
      };
      if (reducedMotion || !changed) {
        gsap.set(element, { ...position, zIndex: count - slot });
      } else if (index === oldIndex) {
        timeline.to(
          element,
          { y: 100, opacity: 0, duration: 0.3, ease: 'power2.in' },
          0
        );
        timeline.set(
          element,
          { ...position, opacity: 0, zIndex: count - slot },
          0.31
        );
        timeline.to(
          element,
          { opacity: position.opacity, duration: 0.35 },
          0.35
        );
      } else {
        timeline.set(element, { zIndex: count - slot }, 0.15);
        timeline.to(
          element,
          { ...position, duration: 0.65, ease: 'power3.inOut' },
          0.1
        );
      }
    });
    previous.current = activeIndex;
    return () => {
      timeline.kill();
    };
  }, [activeIndex, count, reducedMotion]);

  return (
    <div
      className="lp-card-swap"
      ref={container}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setFocused(false);
      }}
    >
      {cards.map((card, index) => (
        <div
          className="lp-swap-card"
          key={index}
          ref={(node) => {
            refs.current[index] = node;
          }}
          aria-hidden={index !== activeIndex}
          inert={index !== activeIndex ? true : undefined}
          style={{ pointerEvents: index === activeIndex ? 'auto' : 'none' }}
        >
          {card}
        </div>
      ))}
    </div>
  );
}
